import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { API_URL } from '@/config/api';
import { useAuth } from '@/context/auth-context';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MultiSelect } from '@/components/ui/multi-select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Globe, FileText, Play, BarChart2, Code, CheckCircle, XCircle, 
  AlertCircle, Plus, ExternalLink, Settings, Building2, Edit, Chrome
} from 'lucide-react';
import axios from 'axios';
import { SectorAssignmentModal } from '@/components/site/SectorAssignmentModal';
import ChromeExtensionActivation from '@/components/ChromeExtensionActivation';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { trackSimulation } from '@/utils/tracking';

interface Site {
  id: string;
  url: string;
  name: string;
  sector?: string;
  sector_ids?: string[];
  features?: {
    chatbot?: boolean;
    voicebot?: boolean;
    suggestions?: boolean;
    analytics?: boolean;
  };
  analytics_integrations?: {
    googleAnalytics?: boolean;
    matomo?: boolean;
    snowplow?: boolean;
  };
  status?: string;
  pages_count?: number;
  created_at?: string;
  last_crawled?: string;
  homepage_screenshot?: string;
}

interface Sector {
  id: string;
  name: string;
  description?: string;
}

export default function MySites() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [selectedSite, setSelectedSite] = useState<string | null>(null);
  const [sectors, setSectors] = useState<Sector[]>([]);
  const [updatingSectors, setUpdatingSectors] = useState<string | null>(null);
  const [sectorModalOpen, setSectorModalOpen] = useState(false);
  const [sectorModalSite, setSectorModalSite] = useState<Site | null>(null);
  const [chromeExtensionSite, setChromeExtensionSite] = useState<Site | null>(null);

  // Fetch user's sites
  const { data: sites, isLoading, refetch } = useQuery<Site[]>({
    queryKey: ['my-sites'],
    queryFn: async () => {
      const response = await axios.get(`${API_URL}/sites/my-sites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const sitesData = response.data;
      
      // Fetch homepage screenshots for each site
      const sitesWithScreenshots = await Promise.all(
        sitesData.map(async (site: Site) => {
          try {
            const screenshotResponse = await axios.get(
              `${API_URL}/sites/${site.id}/homepage-screenshot`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            
            const { screenshot_url, thumbnail_url } = screenshotResponse.data;
            
            // Also get favicon as fallback
            const faviconUrl = `https://www.google.com/s2/favicons?domain=${new URL(site.url).hostname}&sz=128`;
            
            return {
              ...site,
              homepage_screenshot: screenshot_url,
              homepage_thumbnail: thumbnail_url,
              faviconUrl
            };
          } catch (error) {
            console.error(`Failed to fetch homepage screenshot for site ${site.id}:`, error);
            // Return site with no screenshot but with favicon
            const faviconUrl = `https://www.google.com/s2/favicons?domain=${new URL(site.url).hostname}&sz=128`;
            return {
              ...site,
              homepage_screenshot: null,
              homepage_thumbnail: null,
              faviconUrl
            };
          }
        })
      );
      
      return sitesWithScreenshots;
    },
    enabled: !!token
  });

  // Fetch available sectors
  useEffect(() => {
    const fetchSectors = async () => {
      try {
        const response = await axios.get(`${API_URL}/sectors`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('Fetched sectors:', response.data);
        setSectors(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Failed to fetch sectors:', error);
      }
    };
    
    if (token) {
      fetchSectors();
    }
  }, [token]);

  const getStatusBadge = (status?: string) => {
    switch (status) {
      case 'active':
        return <Badge variant="success"><CheckCircle className="w-3 h-3 mr-1" />Active</Badge>;
      case 'inactive':
        return <Badge variant="secondary"><XCircle className="w-3 h-3 mr-1" />Inactive</Badge>;
      case 'pending':
        return <Badge variant="warning"><AlertCircle className="w-3 h-3 mr-1" />Pending</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  const getAnalyticsStatus = (site: Site) => {
    const integrations = site.analytics_integrations || {};
    const activeCount = Object.values(integrations).filter(v => v).length;
    
    if (activeCount === 0) {
      return <Badge variant="secondary">No Analytics</Badge>;
    } else if (activeCount === 1) {
      return <Badge variant="outline">1 Integration</Badge>;
    } else {
      return <Badge variant="success">{activeCount} Integrations</Badge>;
    }
  };

  const handleSectorUpdate = async (siteId: string, sectorIds: string[]) => {
    console.log('Updating sectors for site:', siteId, 'with sector IDs:', sectorIds);
    console.log('Available sectors:', sectors);
    
    setUpdatingSectors(siteId);
    try {
      // Update site with sector_ids
      await axios.put(
        `${API_URL}/sites/${siteId}`,
        { sector_ids: sectorIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      refetch();
    } catch (error) {
      console.error('Failed to update sectors:', error);
    } finally {
      setUpdatingSectors(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">My Sites</h1>
          <p className="text-muted-foreground">Manage your PageMind-enabled websites</p>
        </div>
        <Button onClick={() => {
          const authToken = localStorage.getItem('auth_token');
          const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
          const onboardingUrl = isLocalhost 
            ? 'http://localhost:3101' 
            : 'https://onboarding.pagemind.ai';
          
          // Pass auth token as URL parameter
          const urlWithAuth = authToken 
            ? `${onboardingUrl}?authToken=${encodeURIComponent(authToken)}`
            : onboardingUrl;
          
          window.open(urlWithAuth, '_blank');
        }}>
          <Plus className="w-4 h-4 mr-2" />
          Add New Site
        </Button>
      </div>

      {sites && sites.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <Globe className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold mb-2">No sites yet</h3>
            <p className="text-muted-foreground mb-4">
              Start by adding your first website to PageMind
            </p>
            <Button onClick={() => {
              const authToken = localStorage.getItem('auth_token');
              const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
              const onboardingUrl = isLocalhost 
                ? 'http://localhost:3101' 
                : 'https://onboarding.pagemind.ai';
              
              // Pass auth token as URL parameter
              const urlWithAuth = authToken 
                ? `${onboardingUrl}?authToken=${encodeURIComponent(authToken)}`
                : onboardingUrl;
              
              window.open(urlWithAuth, '_blank');
            }}>
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Site
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {sites?.map((site) => (
            <Card key={site.id} className="hover:shadow-lg transition-shadow overflow-hidden">
              <div className="h-48 w-full overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100 relative">
                {site.homepage_thumbnail || site.homepage_screenshot ? (
                  <img 
                    src={site.homepage_thumbnail || site.homepage_screenshot} 
                    alt={`Screenshot of ${site.name || site.url}`}
                    className="w-full h-full object-cover object-top"
                    onError={(e) => {
                      // On error, hide the image and show the placeholder
                      e.currentTarget.style.display = 'none';
                      const placeholder = e.currentTarget.nextElementSibling;
                      if (placeholder) {
                        (placeholder as HTMLElement).style.display = 'flex';
                      }
                    }}
                  />
                ) : null}
                <div 
                  className="absolute inset-0 flex items-center justify-center"
                  style={{ display: (site.homepage_thumbnail || site.homepage_screenshot) ? 'none' : 'flex' }}
                >
                  <div className="text-center">
                    <Globe className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                    <div className="text-gray-600 font-medium">{site.name || new URL(site.url).hostname}</div>
                    {site.faviconUrl && (
                      <img 
                        src={site.faviconUrl} 
                        alt="Site favicon"
                        className="h-8 w-8 mx-auto mt-2"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <CardTitle className="text-lg">{site.name || site.url}</CardTitle>
                    <CardDescription>{site.url}</CardDescription>
                  </div>
                  {getStatusBadge(site.status)}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Pages Count */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Pages</span>
                    <span className="font-medium">{site.pages_count || 0}</span>
                  </div>


                  {/* Sector Selection */}
                  <div>
                    <label className="text-sm text-muted-foreground mb-2 block">Sectors</label>
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        {site.sector_ids && site.sector_ids.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {site.sector_ids.slice(0, 2).map(sectorId => {
                              const sector = sectors.find(s => s.id === sectorId);
                              return sector ? (
                                <Badge key={sectorId} variant="secondary" className="text-xs">
                                  {sector.name}
                                </Badge>
                              ) : null;
                            })}
                            {site.sector_ids.length > 2 && (
                              <Badge variant="outline" className="text-xs">
                                +{site.sector_ids.length - 2} more
                              </Badge>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-muted-foreground">No sectors assigned</span>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSectorModalSite(site);
                          setSectorModalOpen(true);
                        }}
                        className="h-8 w-8 p-0"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="grid grid-cols-2 gap-2 pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/sites/${site.id}`, { state: { from: '/my-sites' } })}
                    >
                      <FileText className="w-4 h-4 mr-1" />
                      View Pages
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/sites/${site.id}`, { state: { defaultTab: 'settings' } })}
                    >
                      <Settings className="w-4 h-4 mr-1" />
                      Settings
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        // Track simulation activity
                        await trackSimulation(site.id);
                        
                        const simulatorUrl = import.meta.env.VITE_SIMULATOR_URL || 
                          (window.location.hostname === 'admin.pagemind.ai' ? 'https://admin.pagemind.ai' : 
                           window.location.hostname.includes('staging') ? 'https://admin.staging.pagemind.ai' : 
                           'http://localhost:3101');
                        window.open(`${simulatorUrl}/simulator/${site.id}`, '_blank');
                      }}
                    >
                      <Play className="w-4 h-4 mr-1" />
                      Simulator
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/sites/${site.id}/javascript`)}
                    >
                      <Code className="w-4 h-4 mr-1" />
                      Integration
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setChromeExtensionSite(site)}
                    >
                      <Chrome className="w-4 h-4 mr-1" />
                      Chrome Ext
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Sector Assignment Modal */}
      {sectorModalSite && (
        <SectorAssignmentModal
          open={sectorModalOpen}
          onOpenChange={setSectorModalOpen}
          siteId={sectorModalSite.id}
          siteName={sectorModalSite.name || sectorModalSite.url}
          currentSectorIds={sectorModalSite.sector_ids || []}
          onUpdate={(newSectorIds) => {
            refetch();
            setSectorModalSite(null);
          }}
        />
      )}

      {/* Chrome Extension Dialog */}
      {chromeExtensionSite && (
        <Dialog open={!!chromeExtensionSite} onOpenChange={(open) => !open && setChromeExtensionSite(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Chrome Extension Setup</DialogTitle>
            </DialogHeader>
            <ChromeExtensionActivation 
              siteId={chromeExtensionSite.id} 
              siteName={chromeExtensionSite.name || chromeExtensionSite.url} 
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}